import React, { useState, useEffect } from 'react';
import { devportalApi } from '../../../utils/devportalApi';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../../contexts/UserStoreContext';
import { useNavigate } from 'react-router-dom';

const PlanManagement = () => {
    const { user } = useAuth();
    const { t } = useTranslation('developer-portal');
    const navigate = useNavigate();
    const { createInvoice } = useUserStore();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingPlanId, setProcessingPlanId] = useState(null);

    const [currentPlanId, setCurrentPlanId] = useState(null);
    const [currentPlanDetails, setCurrentPlanDetails] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingPlan, setPendingPlan] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [plansData, userData] = await Promise.all([
                devportalApi.getUserPlans(),
                devportalApi.getUserInfo()
            ]);



            // Sort plans by price
            const sortedPlans = plansData.sort((a, b) => a.price - b.price);
            setPlans(sortedPlans);

            if (userData?.current_plan?.id) {
                setCurrentPlanId(userData.current_plan.id);
                setCurrentPlanDetails(userData.current_plan);
            }
        } catch (err) {
            console.error('Failed to fetch data:', err);
            setError('Failed to load subscription details. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpgrade = async (plan) => {
        // Check if current plan has > 5 days remaining
        if (currentPlanDetails) {
            const activeUntil = currentPlanDetails.ActiveUntil || currentPlanDetails.active_until || currentPlanDetails.activeUntil;
            if (activeUntil) {
                const expiryDate = new Date(activeUntil);
                const now = new Date();
                const diffTime = expiryDate - now;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays > 5) {
                    setPendingPlan(plan);
                    setShowConfirmation(true);
                    return;
                }
            }
        }

        processUpgrade(plan);
    };

    const confirmUpgrade = () => {
        if (pendingPlan) {
            processUpgrade(pendingPlan);
            setShowConfirmation(false);
            setPendingPlan(null);
        }
    };

    const cancelUpgrade = () => {
        setShowConfirmation(false);
        setPendingPlan(null);
    };

    const processUpgrade = async (plan) => {
        setProcessingPlanId(plan.id);
        try {
            debugger
            // Check if plan has active invoice
            if (plan.hasActiveInvoice) {
                // Navigate to invoice details page for active invoice
                console.log('Looking for active invoice for plan:', plan.id);
                const userPlans = await devportalApi.getUserPlans();
                console.log('User plans:', userPlans);
                console.log('Plan details:', userPlans.map(p => ({
                    id: p.id,
                    plan_id: p.plan_id,
                    status: p.status,
                    invoice_status: p.invoice_status,
                    tenant_invoice_status: p.tenant?.invoice_status,
                    tenant: p.tenant
                })));
                
                // const activeInvoice = userPlans.find(p => {
                //     console.log('Checking invoice:', p);
                    
                //     // Check multiple possible ways to match plan with invoice
                //     const planMatches = p.plan_id === plan.id || 
                //                        p.plan?.id === plan.id || 
                //                        p.id === plan.id;
                    
                //     // Check multiple possible ways to determine if invoice is active
                //     const isActive = p.tenant?.invoice_status === 'active' || 
                //                      p.status === 'active' || 
                //                      p.invoice_status === 'active' ||
                //                      p.tenant_invoice_status === 'active';
                    
                //     console.log('Plan matches:', planMatches, 'Is active:', isActive);
                //     return planMatches && isActive;
                // });
                
                // console.log('Found active invoice:', plan.activeInvoice);
                
                if (plan.activeInvoice) {
                    console.log('Navigating to invoice:', `/invoices/${plan.activeInvoice.id}`);
                    navigate(`/invoices/${plan.activeInvoice.id}`);
                    return;
                }
                
                // Fallback to payment page if no active invoice found
                console.log('No active invoice found, going to payment page');
                navigate(`/payment?plan_id=${plan.id}`);
                return;
            }

            // Create new invoice for plans without active invoice
            const { data, error: createError } = await createInvoice({
                plan_id: plan.id,
                description: `Upgrade to ${plan.name}`
            });

            if (createError) throw createError;

            if (data && data.id) {
                navigate(`/invoices/${data.id}`);
            } else {
                throw new Error('Invalid invoice creation response');
            }
        } catch (err) {
            console.error('Invoice creation failed:', err);
            setError('Failed to create invoice. Please try again.');
        } finally {
            setProcessingPlanId(null);
        }
    };

    const formatPrice = (price) => {
        if (price === 0) return 'Free';
        return `$${(price / 100).toFixed(2)}/mo`;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} />
                <span>{error}</span>
            </div>
        );
    }

    return (
        <div className="space-y-8">


            {/* Current Plan Section */}
            <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-foreground">{t("billing.current_plan")}</h3>
                        <p className="text-sm text-muted-foreground">{t("account_overview.welcome_back")} <span className="font-medium text-primary capitalize">{plans.find(p => p.id === currentPlanId)?.name || 'Free'}</span> {t("billing.current_plan").toLowerCase()}.</p>
                    </div>
                    <div className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full text-xs font-medium flex items-center">
                        <Icon name="CheckCircle" size={14} className="mr-1" />
                        {t("common.active")}
                    </div>
                </div>
                {currentPlanDetails && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-4">
                        {(currentPlanDetails.StartedAt || currentPlanDetails.started_at || currentPlanDetails.startedAt) && (
                            <div>
                                <p className="text-xs text-muted-foreground">Started At</p>
                                <p className="text-sm font-medium text-foreground">
                                    {new Date(currentPlanDetails.StartedAt || currentPlanDetails.started_at || currentPlanDetails.startedAt).toLocaleDateString()}
                                </p>
                            </div>
                        )}
                        {(currentPlanDetails.ActiveUntil || currentPlanDetails.active_until || currentPlanDetails.activeUntil) && (
                            <div>
                                <p className="text-xs text-muted-foreground">Active Until</p>
                                <p className="text-sm font-medium text-foreground">
                                    {new Date(currentPlanDetails.ActiveUntil || currentPlanDetails.active_until || currentPlanDetails.activeUntil).toLocaleDateString()}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Available Plans */}
            <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">{t("billing.upgrade")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {plans.map((plan) => {
                        const isCurrentPlan = plan.id === currentPlanId;
                        const isProcessing = processingPlanId === plan.id;

                        return (
                            <div
                                key={plan.id}
                                className={`bg-card border rounded-xl p-6 flex flex-col transition-all duration-200 ${isCurrentPlan ? 'border-primary ring-1 ring-primary shadow-md' : 'border-border hover:border-primary/50 hover:shadow-elevation-2'
                                    }`}
                            >
                                <div className="mb-4">
                                    <h4 className="text-xl font-bold text-foreground">{plan.name}</h4>
                                    <div className="mt-2">
                                        <span className="text-2xl font-bold text-foreground">{formatPrice(plan.price)}</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-2 min-h-[40px]">{plan.description}</p>
                                </div>

                                <div className="flex-1 space-y-3 mb-6">
                                    {plan.features && plan.features.split(',').map((feature, idx) => (
                                        <div key={idx} className="flex items-start space-x-2">
                                            <Icon name="Check" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-muted-foreground">{feature.trim()}</span>
                                        </div>
                                    ))}
                                </div>

                                {plan.name.toLowerCase() === 'enterprise plan' ? (
                                    <Button
                                        variant="outline"
                                        fullWidth
                                        iconName="MessageSquare"
                                        iconPosition="left"
                                        onClick={() => window.location.href = '/contact-sales'}
                                        className="hover:bg-primary hover:text-primary-foreground transition-colors"
                                    >
                                        Contact Sales
                                    </Button>
                                ) : (
                                    <Button
                                        variant={isCurrentPlan ? "outline" : "default"}
                                        fullWidth
                                        disabled={isCurrentPlan || isProcessing}
                                        onClick={() => !isCurrentPlan && handleUpgrade(plan)}
                                        className="mt-auto"
                                    >
                                        {isProcessing ? (
                                            <div className="flex items-center space-x-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                <span>{t("common.loading")}</span>
                                            </div>
                                        ) : isCurrentPlan ? (
                                            t("billing.current_plan")
                                        ) : plan.hasActiveInvoice ? (
                                            t("billing.payment")
                                        ) : (
                                            t("billing.upgrade")
                                        )}
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmation && (
                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-xl border border-border shadow-elevation-4 max-w-md w-full p-6">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center">
                                <Icon name="AlertTriangle" size={20} className="text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-foreground">Change Plan?</h3>
                            </div>
                        </div>

                        <p className="text-muted-foreground mb-6">
                            Your current plan is active for more than 5 days. Are you sure you want to change it now? The new plan will start immediately.
                        </p>

                        <div className="flex justify-end space-x-3">
                            <Button
                                variant="outline"
                                onClick={cancelUpgrade}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="default"
                                onClick={confirmUpgrade}
                            >
                                Confirm Change
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanManagement;
